import type { ParseKeys, TFunction } from 'i18next'
import type { z } from 'zod'

import {
  absoluteDuration,
  add,
  type Duration,
  formatDuration,
  isAfter,
  isBefore,
} from '@mntn-dev/utilities'

import type { RefinementEffect } from './types.ts'

export type IsDateTooSmallOptions = {
  duration: Duration
}

export const isDateTooSmall = (
  t: TFunction<'validation'>,
  { duration }: IsDateTooSmallOptions
) =>
  ({
    type: 'refinement',
    refinement: (input: Date, ctx: z.RefinementCtx) => {
      const now = new Date()
      const minimumDate = add(now, duration)
      const past = isBefore(minimumDate, now)

      if (isBefore(input, minimumDate)) {
        ctx.addIssue({
          code: 'custom',
          message: t(`refinements.date.too-small.${past ? 'past' : 'future'}`, {
            duration: formatDuration(duration),
          }),
        })
      }
    },
  }) satisfies RefinementEffect<Date>

export type IsDateTooLargeOptions = {
  duration: Duration
}

export const isDateTooLarge = (
  t: TFunction<'validation'>,
  { duration }: IsDateTooLargeOptions
) =>
  ({
    type: 'refinement',
    refinement: (input: Date, ctx: z.RefinementCtx) => {
      const now = new Date()
      const maximumDate = add(now, duration)
      const past = isBefore(maximumDate, now)

      if (isAfter(input, maximumDate)) {
        ctx.addIssue({
          code: 'custom',
          message: t(`refinements.date.too-large.${past ? 'past' : 'future'}`, {
            duration: formatDuration(absoluteDuration(duration)),
          }),
        })
      }
    },
  }) satisfies RefinementEffect<Date>

export type IsDateLessThanOptions<T extends Record<string, unknown>> = {
  values: T
  field: keyof T
  compareField: keyof T
  translationKeys: {
    field: ParseKeys<'validation'>
    compareField: ParseKeys<'validation'>
  }
}

export const isDateLessThan = <T extends Record<string, unknown>>(
  t: TFunction<'validation'>,
  { field, compareField, translationKeys }: IsDateLessThanOptions<T>
) =>
  ({
    type: 'refinement',
    refinement: (input: T, ctx: z.RefinementCtx) => {
      if (input[field] < input[compareField]) {
        ctx.addIssue({
          code: 'custom',
          message: t('refinements.date.less-than-date', {
            compareField: t(translationKeys.compareField),
          }),
          params: { field },
        })
      }
    },
  }) satisfies RefinementEffect<T>

export const isDateInFuture = (t: TFunction<'validation'>) =>
  ({
    type: 'refinement',
    refinement: (input: Date, ctx: z.RefinementCtx) => {
      if (isBefore(input, new Date())) {
        ctx.addIssue({
          code: 'custom',
          message: t('refinements.date.in-future'),
        })
      }
    },
  }) satisfies RefinementEffect<Date>
